class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.modal = this.closest('.search-modal');
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.searchTerm = '';
    this.hasTrackedSessionOpen = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    const closeButton = this.querySelector('button[type="button"]');
    const resetButton = this.querySelector('button[type="reset"]');

    form?.addEventListener('submit', this.onFormSubmit.bind(this));
    closeButton?.addEventListener('click', this.close.bind(this));
    resetButton?.addEventListener('click', this.clear.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));

    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getWidgetLocation() {
    if (this.closest('.search-modal')) return 'modal';
    if (this.closest('.menu-drawer')) return 'mobile_menu';
    if (document.body.classList.contains('template-search')) return 'search_page';

    return 'site_search';
  }

  getBaseEventPayload(extraPayload = {}) {
    const query = this.getQuery();

    return {
      widget_location: this.getWidgetLocation(),
      search_term: query,
      query_length: query.length,
      page_type: document.body.className,
      page_location: window.location.href,
      ...extraPayload
    };
  }

  pushAnalyticsEvent(eventName, extraPayload = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      ...this.getBaseEventPayload(extraPayload)
    });
  }

  getResultsContainer() {
    return this.predictiveSearchResults.querySelector('#predictive-search__results-list');
  }

  getResultsSummary() {
    const resultsContainer = this.getResultsContainer();
    const totalResults = Number(resultsContainer?.dataset.totalResults || 0);

    return {
      total_results: totalResults,
      product_results: Number(resultsContainer?.dataset.productCount || 0),
      query_results: Number(resultsContainer?.dataset.queryCount || 0),
      collection_results: Number(resultsContainer?.dataset.collectionCount || 0),
      page_results: Number(resultsContainer?.dataset.pageCount || 0),
      article_results: Number(resultsContainer?.dataset.articleCount || 0),
      has_results: totalResults > 0
    };
  }

  getResultType(element) {
    const listItem = element.closest('li[id], #predictive-search-option-search-keywords');
    const itemId = listItem?.id || '';

    if (element.classList.contains('predictive-search__item--term') || itemId === 'predictive-search-option-search-keywords') {
      return 'search_term';
    }
    if (itemId.includes('query-')) return 'query';
    if (itemId.includes('collection-')) return 'collection';
    if (itemId.includes('page-')) return 'page';
    if (itemId.includes('article-')) return 'article';
    if (/^predictive-search-option-\d+$/.test(itemId)) return 'product';

    return 'unknown';
  }

  getResultPosition(element) {
    const listItem = element.closest('li');

    if (!listItem || !listItem.parentElement) return null;

    return Array.from(listItem.parentElement.children).indexOf(listItem) + 1;
  }

  getResultLabel(element) {
    const heading = element.querySelector('.predictive-search__item-heading');
    return heading?.textContent?.trim() || element.textContent.trim();
  }

  trackSessionOpen() {
    if (this.hasTrackedSessionOpen || this.getWidgetLocation() === 'modal') return;

    this.hasTrackedSessionOpen = true;
    this.pushAnalyticsEvent('search_widget_open');
  }

  trackSessionClose(reason = 'close') {
    if (!this.hasTrackedSessionOpen || this.getWidgetLocation() === 'modal') return;

    this.pushAnalyticsEvent('search_widget_close', { close_reason: reason });
    this.hasTrackedSessionOpen = false;
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.clear();
      this.close(true);
      return;
    }

    this.pushAnalyticsEvent('search_widget_search');

    this.getSearchResults(this.searchTerm);
  }
  
  onFormSubmit(event) {
    const query = this.getQuery();
    const selectedResult = this.querySelector('[aria-selected="true"] a');

    if (!query.length || selectedResult) {
      event.preventDefault();
      return;
    }

    this.pushAnalyticsEvent('search_widget_submit');
  }

  onFocus() {
    document.body.classList.add('predictive-search--focus');
    this.trackSessionOpen();
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  } 

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onClick(event) {
    const resultElement = event.target.closest('a.predictive-search__item, button.predictive-search__item--term');

    if (!resultElement || !this.contains(resultElement)) return;

    this.pushAnalyticsEvent('search_widget_result_click', {
      result_type: this.getResultType(resultElement),
      result_position: this.getResultPosition(resultElement),
      result_label: this.getResultLabel(resultElement),
      ...this.getResultsSummary()
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.clear(event);
      this.close(true);
    }
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
     
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      /*
      if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      */
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;
    
    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = ''; 

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);
 
    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    const perPage = this.dataset.perPage || 4;
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      if (this.modal) this.modal.classList.add('searching');
      return;
    }

    fetch(`${theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[limit]')}=${perPage}&${encodeURIComponent('resources[limit_scope]')}=each&section_id=predictive-search`)
      .then((response) => { 
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;

        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          }
        );
        /*
        this.cachedResults[queryKey] = resultsMarkup;
        */
        this.renderSearchResults(resultsMarkup);
        
        if (this.modal) this.modal.classList.add('searching');
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        
        this.close();
        throw error;
      }); 
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    
    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);  

    this.setLiveRegionResults();
    const resultsSummary = this.getResultsSummary();
    this.pushAnalyticsEvent(resultsSummary.has_results ? 'search_widget_results' : 'search_widget_no_results', resultsSummary);
    this.open();
  }

  setLiveRegionResults() { 
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }
  
  getResultsMaxHeight() {
    const drawer = this.closest('.menu-drawer');

    if (drawer && this.predictiveSearchResults) {
      this.resultsMaxHeight = Math.max(180, window.innerHeight - this.predictiveSearchResults.getBoundingClientRect().top - 24);
      return this.resultsMaxHeight;
    }

    this.resultsMaxHeight = window.innerHeight - document.querySelector('.header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    document.body.classList.add('predictive-search--focus');
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
    this.trackSessionClose(clearSearchTerm ? 'clear' : 'blur');
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    document.body.classList.remove('predictive-search--focus');
  }

  clear(event) {
    event?.preventDefault();
    const hadQuery = this.getQuery().length > 0;
    
    this.input.value = '';
    this.removeAttribute('open');
    this.removeAttribute('results');
    this.input.focus();

    if (this.modal) this.modal.classList.remove('searching');

    if (hadQuery) {
      this.pushAnalyticsEvent('search_widget_clear');
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
